/**
 * Device and Platform Detection Utility for PWA
 * Handles robust detection for iOS, iPadOS (including desktop Safari user-agents),
 * Safari vs. non-Safari browsers, Android, Desktop, and Standalone (installed) PWA mode.
 */

export type DevicePlatform = 'standalone' | 'ios-safari' | 'ios-other' | 'android' | 'desktop';

/**
 * Check if the current environment is running in standalone PWA mode
 */
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;

  const navigatorRef = window.navigator as any;
  const isIosStandalone = 'standalone' in navigatorRef && Boolean(navigatorRef.standalone);
  const isDisplayModeStandalone =
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches);

  const isAndroidReferrer =
    typeof document !== 'undefined' && document.referrer.includes('android-app://');

  return isIosStandalone || isDisplayModeStandalone || isAndroidReferrer;
};

/**
 * Check if device is iOS (iPhone, iPod, or iPad including iPadOS reporting MacIntel with touch)
 */
export const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const isClassicIos = /iPhone|iPad|iPod/i.test(ua);
  // iPadOS 13+ reports platform as 'MacIntel' with touch points
  const isIpadOS =
    (navigator.platform === 'MacIntel' || /Macintosh/i.test(ua)) &&
    Boolean(navigator.maxTouchPoints && navigator.maxTouchPoints > 1);

  return isClassicIos || isIpadOS;
};

/**
 * Check if running in genuine Apple Safari (not Chrome iOS, Firefox iOS, Edge iOS, etc.)
 */
export const isSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const isSafariBrowser = /Safari/i.test(ua);
  const isOtherBrowserOnIos = /CriOS|FxiOS|EdgiOS|OPiOS|mercury|DuckDuckGo|Brave/i.test(ua);

  return isSafariBrowser && !isOtherBrowserOnIos;
};

/**
 * Check if device is iOS running Apple Safari
 */
export const isIOSSafari = (): boolean => {
  return isIOS() && isSafari();
};

/**
 * Check if device is iOS running a third-party browser (e.g. Chrome, Firefox on iOS)
 */
export const isIOSNonSafari = (): boolean => {
  return isIOS() && !isSafari();
};

/**
 * Check if device is Android
 */
export const isAndroid = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
};

/**
 * Get the current device platform category
 */
export const getDevicePlatform = (): DevicePlatform => {
  if (isStandalone()) return 'standalone';
  if (isIOSSafari()) return 'ios-safari';
  if (isIOSNonSafari()) return 'ios-other';
  if (isAndroid()) return 'android';
  return 'desktop';
};
